import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import { useRef } from "react";
import { formatFileSize } from "views/admin/Clients/documents/clientDocuments.utils";

const UploadItemCard = ({ item, busy, onUpload, onRemove, onPreview }) => {
  const inputRef = useRef(null);
  const files = item.files || [];
  const atCap = item.maxFiles !== null && item.maxFiles !== undefined && files.length >= item.maxFiles;

  const pickFile = (event) => {
    const file = event.target.files?.[0];
    // Reset first so choosing the same file twice still fires a change event.
    event.target.value = "";
    if (file) onUpload(item, file);
  };

  return (
    <Card variant="outlined">
      <CardContent>
        <Stack spacing={1.5}>
          <Box className="flex items-start justify-between gap-2">
            <Box>
              <Typography variant="subtitle1">{item.label}</Typography>
              {item.instructions && (
                <Typography variant="body2" color="text.secondary">
                  {item.instructions}
                </Typography>
              )}
              {item.flagReason && (
                <Alert severity="warning" className="mt-2">
                  {item.flagReason}
                </Alert>
              )}
            </Box>
            <Chip
              size="small"
              label={item.required ? "Required" : "Optional"}
              color={item.required ? "warning" : "default"}
            />
          </Box>

          {files.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No file uploaded yet.
            </Typography>
          ) : (
            files.map((file) => (
              <Box
                key={file.id}
                className="flex items-center justify-between gap-2 border border-app-border rounded px-2 py-1"
              >
                <Box className="min-w-0">
                  <Typography variant="body2" className="truncate">
                    {file.filename}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formatFileSize(file.sizeBytes)}
                    {file.addedByStaff ? " · Added by your consultant" : ""}
                  </Typography>
                </Box>
                <Box className="flex shrink-0">
                  <IconButton
                    size="small"
                    onClick={() => onPreview(file)}
                    aria-label={`View ${file.filename}`}
                  >
                    <VisibilityOutlinedIcon fontSize="small" />
                  </IconButton>
                  {file.removable && (
                    <IconButton
                      size="small"
                      disabled={busy}
                      onClick={() => onRemove(item, file)}
                      aria-label={`Remove ${file.filename}`}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
              </Box>
            ))
          )}

          <Box>
            <input ref={inputRef} type="file" hidden onChange={pickFile} />
            <Button
              size="small"
              startIcon={<UploadFileIcon />}
              disabled={busy || atCap}
              onClick={() => inputRef.current?.click()}
            >
              {files.length > 0 ? "Upload another" : "Upload file"}
            </Button>
            {atCap && (
              <Typography variant="caption" color="text.secondary" className="ml-2">
                Limit of {item.maxFiles} reached.
                {files.some((file) => file.removable) ? " Remove one to upload a different file." : ""}
              </Typography>
            )}
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
};

export default UploadItemCard;
